import React from 'react';

/**
 * Triggers a one-shot shine flash whenever `value` changes (after first mount).
 * Returns an `active` flag plus a `key` that increments on every change so the
 * overlay remounts and the CSS sweep replays even on rapid consecutive edits.
 */
function useValueFlash(value: string): { active: boolean; key: number } {
  const [state, setState] = React.useState({ active: false, key: 0 });
  const prev = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (prev.current !== null && prev.current !== value) {
      setState((s) => ({ active: true, key: s.key + 1 }));
      const t = setTimeout(() => setState((s) => ({ ...s, active: false })), 650);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);

  return state;
}

interface OrderSummaryProps {
  subtotal: string;
  taxes: string;
  surcharges: string;
  total: string;
  flash?: boolean;
  labelOffset?: string;
  labelWidth?: string;
  valueWidth?: string;
  trailingWidth?: string;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  subtotal,
  taxes,
  surcharges,
  total,
  flash,
  labelOffset,
  labelWidth,
  valueWidth,
  trailingWidth,
}) => {
  const hasLayout = labelOffset !== undefined;

  // Each field flashes independently when its own value changes; the `flash`
  // prop still forces all three (e.g. bulk price-list apply).
  const subtotalFlash = useValueFlash(subtotal);
  const taxesFlash = useValueFlash(taxes);
  const totalFlash = useValueFlash(total);

  const subtotalRow = (label: string, value: string, fieldFlash: { active: boolean; key: number }) => (
    <div className="flex items-center font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
      {hasLayout && <div style={{ flex: '1 1 auto' }} />}
      <p className="shrink-0 text-right" style={labelWidth ? { width: labelWidth } : undefined}>{label}</p>
      <div
        className="relative text-right overflow-hidden shrink-0"
        style={valueWidth ? { width: valueWidth } : { minWidth: '80px' }}
      >
        {(flash || fieldFlash.active) && <span key={fieldFlash.key} className="price-shine-overlay" />}
        {value}
      </div>
      {hasLayout && trailingWidth && <div style={{ width: trailingWidth }} />}
    </div>
  );

  const totalBlock = (
    <div className="flex items-center font-sans font-semibold tracking-[-0.14px] bg-zinc-200 dark:bg-zinc-700 border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-100 rounded-sm py-2 pr-2">
      {hasLayout && <div style={{ flex: '1 1 auto' }} />}
      <p className="shrink-0 text-right text-[10px] font-semibold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 pl-3" style={labelWidth ? { width: labelWidth } : undefined}>Total</p>
      <div className="relative text-right overflow-hidden shrink-0 text-sm font-bold tabular-nums" style={valueWidth ? { width: valueWidth } : { minWidth: '80px' }}>
        {(flash || totalFlash.active) && <span key={totalFlash.key} className="price-shine-overlay" />}
        {total}
      </div>
      {hasLayout && trailingWidth && <div style={{ width: trailingWidth }} />}
    </div>
  );

  if (hasLayout) {
    return (
      <div className="flex flex-col gap-0.5 w-full">
        {subtotalRow('Subtotal', subtotal, subtotalFlash)}
        {subtotalRow('Impuestos', taxes, taxesFlash)}
        <div>{totalBlock}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5 items-end w-full">
      {subtotalRow('Subtotal', subtotal, subtotalFlash)}
      {subtotalRow('Impuestos', taxes, taxesFlash)}
      <div className="w-fit">{totalBlock}</div>
    </div>
  );
};

export default OrderSummary;
